import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-charcoal text-gray-500 py-12 text-center text-sm border-t border-gray-800" data-id="element-54">
        <p className="mb-4" data-id="element-55">
          © {new Date().getFullYear()} The Meltdown Vault. All rights reserved.
        </p>
        <div className="flex items-center justify-center gap-6" data-id="element-56">
          <button onClick={() => setActiveModal('privacy')} className="text-gray-400 hover:text-amber transition-colors duration-200 underline underline-offset-2" data-id="element-57">
            Privacy Policy
          </button>
          <span className="text-gray-600" data-id="element-58">|</span>
          <button onClick={() => setActiveModal('terms')} className="text-gray-400 hover:text-amber transition-colors duration-200 underline underline-offset-2" data-id="element-59">
            Terms of Service
          </button>
        </div>
      </footer>

      <AnimatePresence data-id="element-60">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal/70 backdrop-blur-sm" onClick={() => setActiveModal(null)} data-id="element-61">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-62">
              <div className="sticky top-0 bg-white border-b border-cream-dark px-8 py-5 flex items-center justify-between rounded-t-2xl" data-id="element-63">
                <h2 className="text-2xl font-serif text-charcoal" data-id="element-64">
                  {activeModal === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
                <button onClick={() => setActiveModal(null)} className="text-charcoal-light hover:text-charcoal transition-colors p-1" data-id="element-65">
                  <X className="w-6 h-6" data-id="element-66" />
                </button>
              </div>

              <div className="px-8 py-8 text-charcoal-light leading-relaxed space-y-6" data-id="element-67">
                {activeModal === 'privacy' ? <PrivacyContent data-id="element-68" /> : <TermsContent data-id="element-69" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}
function PrivacyContent() {
  return <>
      <p className="text-sm text-charcoal-light/60" data-id="element-70">Last updated: April 2026</p>

      <h3 className="text-xl font-serif text-charcoal mt-6" data-id="element-71">
        1. Information We Collect
      </h3>
      <p data-id="element-72">
        When you purchase or interact with The Meltdown Vault, we may collect
        personal information such as your name and email address. We collect
        this information solely to deliver your purchased products and
        communicate important updates.
      </p>

      <h3 className="text-xl font-serif text-charcoal" data-id="element-73">
        2. How We Use Your Information
      </h3>
      <p data-id="element-74">
        Your information is used exclusively to process your order, deliver
        digital products, and send relevant communications about your purchase.
        We do not sell, rent, or share your personal information with third
        parties for marketing purposes.
      </p>

      <h3 className="text-xl font-serif text-charcoal" data-id="element-75">3. Data Security</h3>
      <p data-id="element-76">
        We implement industry-standard security measures to protect your
        personal information. All transactions are processed through secure,
        encrypted payment processors. We do not store payment card details on
        our servers.
      </p>

      <h3 className="text-xl font-serif text-charcoal" data-id="element-77">4. Cookies</h3>
      <p data-id="element-78">
        Our website may use cookies to improve your browsing experience and
        analyze site traffic. You can control cookie settings through your
        browser preferences.
      </p>

      <h3 className="text-xl font-serif text-charcoal" data-id="element-79">5. Your Rights</h3>
      <p data-id="element-80">
        You have the right to access, correct, or delete your personal
        information at any time. To exercise these rights, please reach out to
        us through our website.
      </p>

      <h3 className="text-xl font-serif text-charcoal" data-id="element-81">
        6. Changes to This Policy
      </h3>
      <p data-id="element-82">
        We reserve the right to update this Privacy Policy at any time. Any
        changes will be posted on this page with an updated revision date.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p className="text-sm text-charcoal-light/60" data-id="element-83">Last updated: April 2026</p>

      <h3 className="text-xl font-serif text-charcoal mt-6" data-id="element-84">
        1. Digital Products
      </h3>
      <p data-id="element-85">
        The Meltdown Vault provides digital educational resources for parents
        and caregivers. All products are delivered electronically upon purchase.
        By completing a purchase, you agree to these terms.
      </p>

      <h3 className="text-xl font-serif text-charcoal" data-id="element-86">2. Intended Use</h3>
      <p data-id="element-87">
        Our materials are designed for informational and educational purposes
        only. They are not a substitute for professional medical advice,
        diagnosis, or treatment. Always seek the guidance of a qualified
        healthcare provider with any questions regarding your child's health or
        development.
      </p>

      <h3 className="text-xl font-serif text-charcoal" data-id="element-88">3. Refund Policy</h3>
      <p data-id="element-89">
        We offer a full 30-day money-back guarantee on all purchases. If you are
        not satisfied with your purchase for any reason, you may request a
        complete refund within 30 days of your purchase date. No questions
        asked.
      </p>

      <h3 className="text-xl font-serif text-charcoal" data-id="element-90">
        4. Intellectual Property
      </h3>
      <p data-id="element-91">
        All content, materials, and resources provided by The Meltdown Vault are
        protected by copyright. You may not reproduce, distribute, or resell any
        materials without prior written consent. Your purchase grants you a
        personal, non-transferable license to use the materials.
      </p>

      <h3 className="text-xl font-serif text-charcoal" data-id="element-92">
        5. Limitation of Liability
      </h3>
      <p data-id="element-93">
        The Meltdown Vault and its creators shall not be held liable for any
        outcomes resulting from the application of the strategies and
        information provided in our materials. Results may vary based on
        individual circumstances.
      </p>

      <h3 className="text-xl font-serif text-charcoal" data-id="element-94">6. Changes to Terms</h3>
      <p data-id="element-95">
        We reserve the right to modify these Terms of Service at any time.
        Continued use of our products after changes constitutes acceptance of
        the updated terms.
      </p>
    </>;
}